import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Calculator, Hourglass } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, Field, InlineAlert, Input, Skeleton, SourceText, Textarea, useSave } from "@ncp/ui";
import { useCalculationMethod } from "../case/figures";

interface MethodApprovalRecord { reference: string; referenceAr?: string | null; approvedOn: string; methodVersion: string; recordedByName: string; recordedByRole?: string | null; recordedAt: string; note?: string | null }
interface MethodApprovalView {
  version: string; approved: boolean; record?: MethodApprovalRecord | null;
  pendingRelease?: { title: string; status: string; version?: string | null; fromVersion?: string | null } | null;
  mayRecord: boolean; roles: string[]; minReferenceLength: number;
}

/**
 * The Department's approval of the calculation method (condition 2.16). Every figure the platform shows is computed by the published, versioned
 * method, which reads "Proposed" until the Department approves the version in force. A legal expert, the Director, the Assistant Under-Secretary or
 * the Under-Secretary – never an administrator – records the approval here with the reference and date of the Department's written approval; it is
 * audited, and it holds for that version only: the next version a release publishes reads "Proposed" again.
 */
export function MethodApprovalTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const api = useApi();
  const qc = useQueryClient();
  const view = useQuery({ queryKey: ["method-approval"], queryFn: () => api.get<MethodApprovalView>("/api/analysis/method/approval") });
  const method = useCalculationMethod();
  const [form, setForm] = useState({ reference: "", referenceAr: "", approvedOn: "", note: "" });
  const record = useSave({
    mutationFn: () => api.post("/api/analysis/method/approval", { reference: form.reference.trim(), referenceAr: form.referenceAr.trim() || null, approvedOn: form.approvedOn || null, note: form.note.trim() || null }),
    saved: t("backoffice:decisionControls.method.recorded"),
    onSuccess: () => { setForm({ reference: "", referenceAr: "", approvedOn: "", note: "" }); for (const k of [["method-approval"], ["analysis", "method"]]) void qc.invalidateQueries({ queryKey: k }); },
  });
  const v = view.data;
  if (view.isLoading || !v) return <Skeleton className="h-64" />;
  const r = v.record;
  const earlier = r && r.methodVersion !== v.version ? r : null;
  const valid = form.reference.trim().length >= v.minReferenceLength && !!form.approvedOn;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("backoffice:decisionControls.method.hint")}</p>
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Calculator className="size-4 text-aegold-700" aria-hidden />
          <p className="text-sm font-semibold">{t("backoffice:decisionControls.method.version", { version: v.version })}</p>
          {v.approved ? <Badge size="sm" variant="success"><BadgeCheck /> {t("backoffice:decisionControls.method.approved")}</Badge> : <Badge size="sm" variant="warning"><Hourglass /> {t("backoffice:decisionControls.method.proposed")}</Badge>}
        </div>
        {v.approved && r ? (
          <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_minmax(0,1fr)]" data-method-approval={r.methodVersion}>
            <dt className="text-muted-foreground">{t("backoffice:decisionControls.method.reference")}</dt><dd dir="auto"><SourceText text={ar && r.referenceAr ? r.referenceAr : r.reference} /></dd>
            <dt className="text-muted-foreground">{t("backoffice:decisionControls.method.approvedOn")}</dt><dd>{formatDate(r.approvedOn, locale)}</dd>
            <dt className="text-muted-foreground">{t("backoffice:decisionControls.method.recordedBy")}</dt><dd><SourceText text={r.recordedByName} />{r.recordedByRole ? ` (${t(`roles.${r.recordedByRole}`)})` : ""} · {formatDateTime(r.recordedAt, locale)}</dd>
            {r.note ? <><dt className="text-muted-foreground">{t("backoffice:decisionControls.method.note")}</dt><dd dir="auto"><SourceText text={r.note} /></dd></> : null}
          </dl>
        ) : (
          <InlineAlert tone="warning">{earlier
            ? t("backoffice:decisionControls.method.earlierApproved", { earlier: earlier.methodVersion, reference: ar && earlier.referenceAr ? earlier.referenceAr : earlier.reference, date: formatDate(earlier.approvedOn, locale), version: v.version })
            : t("backoffice:decisionControls.method.notApproved", { version: v.version })}</InlineAlert>
        )}
        {v.pendingRelease ? <p className="text-xs text-muted-foreground">{t("backoffice:decisionControls.method.pendingRelease", { version: v.pendingRelease.version ?? "—", status: t(`backoffice:decisionControls.method.releaseStatus.${v.pendingRelease.status}`, { defaultValue: v.pendingRelease.status }) })}</p> : null}
      </Card>

      {!v.approved ? (
        v.mayRecord ? (
          <Card className="space-y-3 p-4">
            <p className="text-sm font-semibold">{t("backoffice:decisionControls.method.recordTitle", { version: v.version })}</p>
            <p className="text-xs text-muted-foreground">{t("backoffice:decisionControls.method.recordHint")}</p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label={t("backoffice:decisionControls.method.reference")} hint={t("backoffice:decisionControls.method.referenceHint")} required>
                {(id) => <Input id={id} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} dir="auto" />}
              </Field>
              <Field label={t("backoffice:decisionControls.method.referenceAr")}>
                {(id) => <Input id={id} value={form.referenceAr} onChange={(e) => setForm({ ...form, referenceAr: e.target.value })} dir="rtl" lang="ar" />}
              </Field>
              <Field label={t("backoffice:decisionControls.method.approvedOn")} required>
                {(id) => <Input id={id} type="date" value={form.approvedOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, approvedOn: e.target.value })} />}
              </Field>
              <Field label={t("backoffice:decisionControls.method.note")}>
                {(id) => <Textarea id={id} rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} dir="auto" draft={`legal:method:${v.version}:approval-note`} />}
              </Field>
            </div>
            <div className="flex justify-end">
              <Button variant="gold" disabled={!valid} loading={record.isPending} onClick={() => record.mutate()}><BadgeCheck /> {t("backoffice:decisionControls.method.record", { version: v.version })}</Button>
            </div>
          </Card>
        ) : <p className="text-xs text-muted-foreground">{t("backoffice:decisionControls.method.whoRecords", { roles: v.roles.map((x) => t(`roles.${x}`)).join(ar ? "، " : ", ") })}</p>
      ) : null}

      {method.data ? (
        <Card className="divide-y divide-sand-100">
          <p className="p-3 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:decisionControls.method.measures", { count: method.data.measures.length })}</p>
          {method.data.measures.map((m) => (
            <details key={m.key} className="group p-3 text-sm">
              <summary className="cursor-pointer font-medium">{ar ? m.nameAr : m.nameEn}</summary>
              <p className="mt-1 text-xs text-muted-foreground" dir="auto">{ar ? m.definitionAr : m.definitionEn}</p>
              {m.formula ? <p className="mt-1 font-mono text-[11px] text-sand-700" dir="ltr">{m.formula}</p> : null}
            </details>
          ))}
        </Card>
      ) : null}
    </div>
  );
}
